import numberWithCommas from 'common/util/numberWithCommas';

import type { Timeframe } from './Timeframe';

export function getPricePoint(plan: Plan, trackedUsers: number) {
  if (!plan.pricePoints) {
    return null;
  }

  // find lowest price point that supports the number of tracked users
  return plan.pricePoints.find((pricePoint) => {
    return pricePoint.trackedUsers >= trackedUsers;
  });
}

export type PricePoint = {
  trackedUsers: number;
} & {
  [key in Timeframe]: number;
};

type Feature = string | ((trackedUsers: number) => string);

type ButtonType = 'greenButton' | 'ghostButton';
export type PlanName = 'free' | 'core' | 'pro' | 'business';
export type Plan = {
  name: string;
  planID?: {
    [key in Timeframe]: string;
  };
  description: string;
  features: Feature[];
  pricePoints?: PricePoint[];
  mostPopular?: boolean;
  cta: {
    label: string;
    buttonType: 'greenButton' | 'ghostButton' | ((trackedUsers: number) => ButtonType);
    href: string;
  };
  grid: {
    userTypes: {
      managers: number | null | string;
      contributors: number | null;
      trackedUsers: number | null | string | ((trackedUsers: number) => string);
    };
    feedbackPortal: {
      posts: number | null;
      boards: number | null;
      core: boolean;
      changelog: boolean;
      privateBoards: boolean;
      customDomains: boolean;
      contentTranslations: boolean;
      customStatuses: boolean;
      advancedPortalPrivacy: boolean;
      removeBranding: boolean | 'upsell';
      emailWhitelabel: boolean | 'upsell';
    };
    feedbackManagement: {
      roadmaps: number | null;
      apiWebhooks: boolean;
      internalComments: boolean;
      customFields: boolean;
      userSegmentation: boolean;
      adminRoles: boolean;
      automations: boolean;
    };
    autopilot: {
      autopilotSources: number | null;
      capture: boolean;
      dedupe: boolean;
      smartReplies: boolean;
      commentSummaries: boolean;
    };
    integrations: {
      capture: boolean;
      notifications: boolean;
      segment: boolean;
      projectManagement: boolean;
      zapier: boolean;
      auth: boolean;
      crm: boolean | 'upsell';
    };
    compliance: {
      dataPrivacy: boolean;
      soc2: boolean;
      invoice: boolean;
      securityReview: boolean | 'upsell';
    };
  };
};

const FreePricePoints: PricePoint[] = [
  {
    trackedUsers: 25,
    yearly: 0,
    monthly: 0,
  },
];

const CorePricePoints: PricePoint[] = [
  {
    trackedUsers: 100,
    yearly: 19,
    monthly: 24,
  },
  {
    trackedUsers: 200,
    yearly: 49,
    monthly: 61,
  },
  {
    trackedUsers: 300,
    yearly: 75,
    monthly: 94,
  },
  {
    trackedUsers: 400,
    yearly: 99,
    monthly: 124,
  },
  {
    trackedUsers: 500,
    yearly: 125,
    monthly: 156,
  },
  {
    trackedUsers: 600,
    yearly: 149,
    monthly: 186,
  },
  {
    trackedUsers: 700,
    yearly: 175,
    monthly: 219,
  },
  {
    trackedUsers: 800,
    yearly: 199,
    monthly: 249,
  },
  {
    trackedUsers: 900,
    yearly: 225,
    monthly: 281,
  },
  {
    trackedUsers: 1000,
    yearly: 249,
    monthly: 311,
  },
  {
    trackedUsers: 1250,
    yearly: 275,
    monthly: 344,
  },
  {
    trackedUsers: 1500,
    yearly: 299,
    monthly: 374,
  },
  {
    trackedUsers: 1750,
    yearly: 325,
    monthly: 406,
  },
  {
    trackedUsers: 2000,
    yearly: 349,
    monthly: 436,
  },
  {
    trackedUsers: 2250,
    yearly: 375,
    monthly: 469,
  },
  {
    trackedUsers: 2500,
    yearly: 399,
    monthly: 499,
  },
  {
    trackedUsers: 3000,
    yearly: 425,
    monthly: 531,
  },
  {
    trackedUsers: 4000,
    yearly: 475,
    monthly: 594,
  },
  {
    trackedUsers: 5000,
    yearly: 525,
    monthly: 656,
  },
];

const ProPricePoints: PricePoint[] = [
  {
    trackedUsers: 100,
    yearly: 79,
    monthly: 99,
  },
  {
    trackedUsers: 200,
    yearly: 129,
    monthly: 161,
  },
  {
    trackedUsers: 300,
    yearly: 179,
    monthly: 224,
  },
  {
    trackedUsers: 400,
    yearly: 229,
    monthly: 286,
  },
  {
    trackedUsers: 500,
    yearly: 279,
    monthly: 349,
  },
  {
    trackedUsers: 600,
    yearly: 329,
    monthly: 411,
  },
  {
    trackedUsers: 700,
    yearly: 379,
    monthly: 474,
  },
  {
    trackedUsers: 800,
    yearly: 429,
    monthly: 536,
  },
  {
    trackedUsers: 900,
    yearly: 479,
    monthly: 599,
  },
  {
    trackedUsers: 1000,
    yearly: 529,
    monthly: 661,
  },
  {
    trackedUsers: 1250,
    yearly: 579,
    monthly: 724,
  },
  {
    trackedUsers: 1500,
    yearly: 629,
    monthly: 786,
  },
  {
    trackedUsers: 1750,
    yearly: 679,
    monthly: 849,
  },
  {
    trackedUsers: 2000,
    yearly: 729,
    monthly: 911,
  },
  {
    trackedUsers: 2250,
    yearly: 779,
    monthly: 974,
  },
  {
    trackedUsers: 2500,
    yearly: 829,
    monthly: 1036,
  },
  {
    trackedUsers: 3000,
    yearly: 879,
    monthly: 1099,
  },
  {
    trackedUsers: 4000,
    yearly: 979,
    monthly: 1224,
  },
  {
    trackedUsers: 5000,
    yearly: 1079,
    monthly: 1349,
  },
];

const PricingV4Plans: Record<PlanName, Plan> = {
  free: {
    name: 'Free',
    description: 'Get your feedback platform up and running for free',
    features: ['25 tracked users', 'Unlimited posts', 'Automatic feedback capture'],
    pricePoints: FreePricePoints,
    cta: {
      label: 'Get started',
      buttonType: 'greenButton',
      href: '/register',
    },
    grid: {
      userTypes: {
        managers: 5,
        contributors: null,
        trackedUsers: 25,
      },
      feedbackPortal: {
        posts: null,
        boards: null,
        core: true,
        changelog: true,
        privateBoards: true,
        customDomains: false,
        contentTranslations: false,
        customStatuses: false,
        advancedPortalPrivacy: false,
        removeBranding: false,
        emailWhitelabel: false,
      },
      feedbackManagement: {
        roadmaps: 1,
        apiWebhooks: true,
        internalComments: true,
        customFields: false,
        userSegmentation: false,
        automations: false,
        adminRoles: false,
      },
      autopilot: {
        autopilotSources: null,
        capture: true,
        dedupe: true,
        smartReplies: false,
        commentSummaries: false,
      },
      integrations: {
        capture: true,
        notifications: true,
        segment: false,
        projectManagement: false,
        zapier: false,
        auth: false,
        crm: false,
      },
      compliance: {
        dataPrivacy: true,
        soc2: false,
        invoice: false,
        securityReview: false,
      },
    },
  },
  core: {
    name: 'Core',
    planID: {
      monthly: 'core-monthly',
      yearly: 'core-yearly',
    },
    description: 'Scale your feedback platform to identify customer needs',
    features: [
      (trackedUsers: number) => {
        const plan = PricingV4Plans.core;
        const pricePoint =
          getPricePoint(plan, trackedUsers) || plan.pricePoints![plan.pricePoints!.length - 1];
        const showPlus = pricePoint !== plan.pricePoints![plan.pricePoints!.length - 1];
        return `${numberWithCommas(pricePoint.trackedUsers)}${showPlus ? '+' : ''} tracked users`;
      },
      'Custom domains',
      'Custom statuses',
    ],
    pricePoints: CorePricePoints,
    cta: {
      label: 'Get started',
      buttonType: 'greenButton',
      href: '/register?trial=core',
    },
    grid: {
      userTypes: {
        managers: 5,
        contributors: null,
        trackedUsers: (trackedUsers: number) => {
          const plan = PricingV4Plans.core;
          const pricePoint =
            getPricePoint(plan, trackedUsers) || plan.pricePoints![plan.pricePoints!.length - 1];
          const showPlus = pricePoint !== plan.pricePoints![plan.pricePoints!.length - 1];
          return `${numberWithCommas(pricePoint.trackedUsers)}${showPlus ? '+' : ''}`;
        },
      },
      feedbackPortal: {
        posts: null,
        boards: null,
        core: true,
        changelog: true,
        privateBoards: true,
        customDomains: true,
        contentTranslations: true,
        customStatuses: false,
        advancedPortalPrivacy: false,
        removeBranding: false,
        emailWhitelabel: false,
      },
      feedbackManagement: {
        roadmaps: 1,
        apiWebhooks: true,
        internalComments: true,
        customFields: false,
        userSegmentation: false,
        automations: false,
        adminRoles: false,
      },
      autopilot: {
        autopilotSources: null,
        capture: true,
        dedupe: true,
        smartReplies: false,
        commentSummaries: false,
      },
      integrations: {
        capture: true,
        notifications: true,
        segment: true,
        projectManagement: false,
        zapier: false,
        auth: false,
        crm: false,
      },
      compliance: {
        dataPrivacy: true,
        soc2: false,
        invoice: false,
        securityReview: false,
      },
    },
  },
  pro: {
    name: 'Pro',
    planID: {
      monthly: 'pro-monthly',
      yearly: 'pro-yearly',
    },
    description: 'Turn feedback into action with advanced reporting and workflows',
    mostPopular: true,
    features: [
      (trackedUsers: number) => {
        const plan = PricingV4Plans.pro;
        const pricePoint =
          getPricePoint(plan, trackedUsers) || plan.pricePoints![plan.pricePoints!.length - 1];
        const showPlus = pricePoint !== plan.pricePoints![plan.pricePoints!.length - 1];
        return `${numberWithCommas(pricePoint.trackedUsers)}${showPlus ? '+' : ''} tracked users`;
      },
      'PM integrations',
      'Advanced privacy',
    ],
    pricePoints: ProPricePoints,
    cta: {
      label: 'Get started',
      buttonType: 'greenButton',
      href: '/register?trial=pro',
    },
    grid: {
      userTypes: {
        managers: 10,
        contributors: null,
        trackedUsers: (trackedUsers: number) => {
          const plan = PricingV4Plans.pro;
          const pricePoint =
            getPricePoint(plan, trackedUsers) || plan.pricePoints![plan.pricePoints!.length - 1];
          const showPlus = pricePoint !== plan.pricePoints![plan.pricePoints!.length - 1];
          return `${numberWithCommas(pricePoint.trackedUsers)}${showPlus ? '+' : ''}`;
        },
      },
      feedbackPortal: {
        posts: null,
        boards: null,
        core: true,
        changelog: true,
        privateBoards: true,
        customDomains: true,
        contentTranslations: true,
        customStatuses: true,
        advancedPortalPrivacy: true,
        removeBranding: false,
        emailWhitelabel: false,
      },
      feedbackManagement: {
        roadmaps: null,
        apiWebhooks: true,
        internalComments: true,
        customFields: true,
        userSegmentation: true,
        automations: true,
        adminRoles: false,
      },
      autopilot: {
        autopilotSources: null,
        capture: true,
        dedupe: true,
        smartReplies: true,
        commentSummaries: true,
      },
      integrations: {
        capture: true,
        notifications: true,
        segment: true,
        projectManagement: true,
        zapier: true,
        auth: false,
        crm: false,
      },
      compliance: {
        dataPrivacy: true,
        soc2: true,
        invoice: false,
        securityReview: false,
      },
    },
  },
  business: {
    name: 'Business',
    description: 'More controls and customization for your unique organization',
    features: ['5,000+ tracked users', 'SSO integrations', 'CRM integrations'],
    cta: {
      label: 'Request demo',
      buttonType: (trackedUsers) => {
        if (trackedUsers > 5000) {
          return 'greenButton';
        }
        return 'ghostButton';
      },
      href: '/request-demo',
    },
    grid: {
      userTypes: {
        managers: 'Custom',
        contributors: null,
        trackedUsers: 'Custom',
      },
      feedbackPortal: {
        posts: null,
        boards: null,
        core: true,
        changelog: true,
        privateBoards: true,
        customDomains: true,
        contentTranslations: true,
        customStatuses: true,
        advancedPortalPrivacy: true,
        removeBranding: 'upsell',
        emailWhitelabel: 'upsell',
      },
      feedbackManagement: {
        roadmaps: null,
        apiWebhooks: true,
        internalComments: true,
        customFields: true,
        userSegmentation: true,
        automations: true,
        adminRoles: true,
      },
      autopilot: {
        autopilotSources: null,
        capture: true,
        dedupe: true,
        smartReplies: true,
        commentSummaries: true,
      },
      integrations: {
        capture: true,
        notifications: true,
        segment: true,
        projectManagement: true,
        zapier: true,
        auth: true,
        crm: 'upsell',
      },
      compliance: {
        dataPrivacy: true,
        soc2: true,
        invoice: true,
        securityReview: 'upsell',
      },
    },
  },
};

export default PricingV4Plans;
